import { MultiSelectComboboxField } from '@/components/form-components/MultiSelectCombobox'
import { useRestClient } from '@/contexts/QueryProvider'
import { useMemo } from 'react'
import { Control, FieldValues, Path, useWatch } from 'react-hook-form'
import {
  useWorkspaceProjectApplicationOptions,
  WorkspaceProjectApplicationFields,
} from './WorkspaceProjectApplicationFields'

type WorkspaceFieldsShape = {
  projectId: number
  applicationId: number
  projectTeamIds: number[]
  supportTeamIds: number[]
}

type WorkspaceApplicationTeamsFieldsProps<T extends FieldValues & WorkspaceFieldsShape> = {
  control: Control<T>
  orgId: number | undefined
  lockedProjectId?: number
  excludedAppIds?: number[]
  showProjectPicker?: boolean
}

export function useWorkspaceFormOptions(
  orgId: number | undefined,
  lockedProjectId: number | undefined,
  selectedAppId: number,
  excludedAppIds: number[] = []
) {
  const { getApplicationsApiGetApplicationTeams, getSubscriptionsApiGetOrganisationTeams } = useRestClient()

  const { projectOptions, applicationOptions, loading, error } = useWorkspaceProjectApplicationOptions(
    orgId,
    lockedProjectId,
    excludedAppIds
  )

  const {
    data: projectTeams,
    isPending: isProjectTeamsPending,
    isError: isProjectTeamsError,
  } = getSubscriptionsApiGetOrganisationTeams.useQuery(
    { query: { orgId: orgId! } },
    { enabled: !!orgId }
  )

  const {
    data: supportTeams,
    isPending: isSupportTeamsPending,
    isError: isSupportTeamsError,
  } = getApplicationsApiGetApplicationTeams.useQuery(
    { query: { orgId: orgId!, appId: selectedAppId } },
    { enabled: !!orgId && selectedAppId > 0 }
  )

  const projectTeamOptions = useMemo(
    () =>
      projectTeams?.map((team) => ({
        value: team.id,
        label: team.name,
        description: 'Project Team',
      })) ?? [],
    [projectTeams]
  )

  const supportTeamOptions = useMemo(
    () =>
      supportTeams?.map((team) => ({
        value: team.id,
        label: team.name,
        description: 'Support Team',
      })) ?? [],
    [supportTeams]
  )

  return {
    projectOptions,
    applicationOptions,
    projectTeamOptions,
    supportTeamOptions,
    isProjectTeamsPending,
    isProjectTeamsError,
    isSupportTeamsPending,
    isSupportTeamsError,
    loading,
    error,
  }
}

export function WorkspaceApplicationTeamsFields<T extends FieldValues & WorkspaceFieldsShape>({
  control,
  orgId,
  lockedProjectId,
  excludedAppIds = [],
  showProjectPicker = lockedProjectId == null,
}: WorkspaceApplicationTeamsFieldsProps<T>) {
  const selectedAppId = (useWatch({ control, name: 'applicationId' as Path<T> }) as number | undefined) ?? 0

  const {
    projectTeamOptions,
    supportTeamOptions,
    isProjectTeamsPending,
    isProjectTeamsError,
    isSupportTeamsPending,
    isSupportTeamsError,
  } = useWorkspaceFormOptions(orgId, lockedProjectId, selectedAppId, excludedAppIds)

  return (
    <div className="space-y-6">
      <WorkspaceProjectApplicationFields
        control={control}
        orgId={orgId}
        lockedProjectId={lockedProjectId}
        excludedAppIds={excludedAppIds}
        showProjectPicker={showProjectPicker}
      />

      <MultiSelectComboboxField
        control={control}
        name={'projectTeamIds' as Path<T>}
        label="Project Teams"
        options={projectTeamOptions}
        placeholder="Select project teams..."
        searchPlaceholder="Search project teams..."
        emptyMessage="No project teams found."
        disabled={isProjectTeamsPending || isProjectTeamsError}
      />

      {selectedAppId > 0 && (
        <MultiSelectComboboxField
          control={control}
          name={'supportTeamIds' as Path<T>}
          label="Application Teams"
          options={supportTeamOptions}
          placeholder="Select application teams..."
          searchPlaceholder="Search application teams..."
          emptyMessage="No application teams found."
          disabled={isSupportTeamsPending || isSupportTeamsError}
        />
      )}
    </div>
  )
}
